import React, {
  KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import Typography from "@/ui/Typography/Typography";
import Textarea from "@/ui/Textarea/Textarea";
import { FlexRow } from "@/ui/FlexContainer/FlexContainer";
import Button from "@/ui/Buttons/Button/Button";
import ChatMessage from "@/components/Chat/Message";
import { useChatDataContext } from "@/contexts/ChatContext";
import { ChatMessageI } from "@/api/chat";
import { ShareSurveyBlock } from "@/features/Surveys/ShareSurveyBlock";
import { AttachIcon, PaperPlaneIcon, ChatArrow } from "@/assets/icons";
import { useAuthStore } from "@/store/auth";
import { getPublicChatUsername } from "@/features/Chat/chatIdentity";
import cls from "./styles.module.scss";

export interface ChatProps {
  title?: string;
  desktopPresentation?: "inline" | "docked" | "fullscreen";
  desktopExpanded?: boolean;
  onDesktopExpandedChange?: (expanded: boolean) => void;
  onDesktopFullscreenRequest?: () => void;
  onDesktopFullscreenExit?: () => void;
}

function SoundIcon({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 9v6h4l5 4V5L9 9H5Z" />
      <path d="M17 9.2a4 4 0 0 1 0 5.6M19.5 6.7a7.5 7.5 0 0 1 0 10.6" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 9v6h4l5 4V5L9 9H5Z" />
      <path d="m17 9 5 5m0-5-5 5" />
    </svg>
  );
}

function FullscreenIcon({ fullscreen }: { fullscreen: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path
        d={
          fullscreen
            ? "M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5"
            : "M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"
        }
      />
    </svg>
  );
}

function getReplyLabel(message: ChatMessageI) {
  if (message.text) return message.text;
  if (message.pet) return `Страница питомца ${message.pet.name}`;
  if (message.survey_response) {
    return `Анкета «${message.survey_response.survey.title}»`;
  }
  return "Сообщение";
}

export default function Chat({
  title = "Чат тепла",
  desktopPresentation = "inline",
  desktopExpanded,
  onDesktopExpandedChange,
  onDesktopFullscreenRequest,
  onDesktopFullscreenExit,
}: ChatProps) {
  const {
    messages,
    isLoading,
    input,
    setInput,
    isEditing,
    editingValue,
    setEditingValue,
    cancelEdit,
    replyingTo,
    cancelReply,
    submit,
    isSending,
    onlineCount,
    unreadCount,
    soundEnabled,
    toggleSound,
    markVisibleMessagesRead,
  } = useChatDataContext();

  const isAuthenticated = useAuthStore((state) =>
    Boolean(state.tokenResponse?.access)
  );
  const listRef = useRef<HTMLDivElement | null>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const attachBlockRef = useRef<HTMLDivElement>(null);
  const wasMobile = useRef(window.innerWidth <= 760);

  const [width, setWidth] = useState(window.innerWidth);
  const [localExpanded, setLocalExpanded] = useState(window.innerWidth > 760);
  const messagesId = useId();
  const [attachBlockIsOpen, setAttachBlockIsOpen] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<
    number | null
  >(null);

  const isMobile = width <= 760;
  const docked = !isMobile && desktopPresentation === "docked";
  const chatIsVisible =
    !isMobile && desktopExpanded !== undefined
      ? desktopExpanded
      : localExpanded;
  const isMainPage = useLocation().pathname === "/";
  const isFullscreen =
    !isMobile && desktopPresentation === "fullscreen" && chatIsVisible;

  useEffect(() => {
    const root = chatRef.current;
    if (!isFullscreen || !root) return;
    const scroller = root.closest<HTMLElement>("[data-site-scroll]");
    const previousOverflow = scroller?.style.overflowY;
    const previousFocus =
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body
        ? document.activeElement
        : root.querySelector<HTMLButtonElement>("[data-chat-toggle]");
    const blocked: Array<{ element: HTMLElement; inert: boolean }> = [];
    // Make only the branches outside the existing chat inert, keeping its
    // subtree/provider intact and preventing keyboard access behind it.
    let branch: HTMLElement | null = root;
    while (branch && branch !== scroller && branch !== document.body) {
      const parent: HTMLElement | null = branch.parentElement;
      if (!parent) break;
      for (const sibling of Array.from(parent.children)) {
        if (sibling instanceof HTMLElement && sibling !== branch) {
          blocked.push({ element: sibling, inert: sibling.inert ?? false });
          sibling.inert = true;
        }
      }
      branch = parent;
    }
    if (scroller) scroller.style.overflowY = "hidden";
    root
      .querySelector<HTMLButtonElement>("[data-chat-toggle]")
      ?.focus({ preventScroll: true });
    return () => {
      for (const { element, inert } of blocked) element.inert = inert;
      if (scroller) scroller.style.overflowY = previousOverflow || "";
      if (previousFocus?.isConnected)
        previousFocus.focus({ preventScroll: true });
      else
        root
          .querySelector<HTMLButtonElement>("[data-chat-toggle]")
          ?.focus({ preventScroll: true });
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length, chatIsVisible]);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (width <= 760 && !wasMobile.current) setLocalExpanded(false);
    wasMobile.current = width <= 760;
  }, [width]);

  useEffect(() => {
    if (!chatIsVisible) return;
    const markRead = () => {
      if (document.visibilityState === "visible") {
        void markVisibleMessagesRead();
      }
    };
    markRead();
    document.addEventListener("visibilitychange", markRead);
    return () => document.removeEventListener("visibilitychange", markRead);
  }, [chatIsVisible, markVisibleMessagesRead, messages.length]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (
        attachBlockRef.current &&
        !attachBlockRef.current.contains(event.target as Node)
      ) {
        setAttachBlockIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const grouped = useMemo(() => {
    const result: Array<{
      key: string;
      userId: number | null;
      username: string;
      isOwn: boolean;
      items: ChatMessageI[];
    }> = [];

    messages.forEach((message) => {
      const username = getPublicChatUsername(message.username);
      const processedMessage = { ...message, username };
      const previous = result[result.length - 1];

      if (
        previous &&
        previous.userId === message.user_id &&
        previous.isOwn === message.is_own
      ) {
        previous.items.push(processedMessage);
      } else {
        result.push({
          key: String(message.id),
          userId: message.user_id,
          username,
          isOwn: message.is_own,
          items: [processedMessage],
        });
      }
    });

    return result;
  }, [messages]);

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const handleReplyPreviewClick = (messageId: number) => {
    const element = document.getElementById(`chat-message-${messageId}`);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMessageId(messageId);
    window.setTimeout(() => setHighlightedMessageId(null), 1400);
  };

  const toggleChat = () => {
    if (docked && onDesktopFullscreenRequest) onDesktopFullscreenRequest();
    else if (!isMobile && desktopExpanded !== undefined)
      onDesktopExpandedChange?.(!chatIsVisible);
    else setLocalExpanded((current) => !current);
  };
  const handleFullscreenKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) return;
    // A survey preview is a body portal with its own Escape handler. Its
    // React events bubble here, but it must close before the chat does.
    if (!event.currentTarget.contains(event.target as Node)) return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onDesktopExpandedChange?.(false);
    } else if (event.key === "Tab") {
      const controls = Array.from(
        event.currentTarget.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], textarea:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]'
        )
      ).filter((element) => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  const sendDisabled =
    isSending || !(isEditing ? editingValue.trim() : input.trim());

  return (
    <div
      ref={chatRef}
      role={isFullscreen ? "dialog" : undefined}
      aria-modal={isFullscreen ? true : undefined}
      aria-label={isFullscreen ? title : undefined}
      onKeyDown={isFullscreen ? handleFullscreenKeyDown : undefined}
      data-chat-expanded={chatIsVisible}
      data-chat-layout={isMobile ? "mobile" : desktopPresentation}
      data-chat-managed={!isMobile && desktopExpanded !== undefined}
      className={`${cls.chatWrapper} ${
        isMobile && !chatIsVisible ? cls.chatWrapperCollapsed : ""
      } ${docked ? cls.desktopDocked : ""}`}
    >
      <div
        className={`${cls.chatContainer} ${
          isMainPage ? cls.chatContainerBLack : cls.chatContainerWhite
        }`}
        style={{
          borderRadius: (isMobile ? chatIsVisible : isMainPage) ? "0" : "32px",
        }}
      >
        <div
          className={cls.header}
          onClick={() => (isMobile || docked || isFullscreen) && toggleChat()}
        >
          <div className={cls.headerInfo}>
            <FlexRow gap={8} alignItems="center">
              <Typography
                size="3xl"
                family="Manrope"
                weight="bold"
                color={isMainPage ? "#fff" : "var(--text-brown)"}
                lineHeight={1.1}
                className={cls.headerLabel}
              >
                {title}
              </Typography>
              {unreadCount > 0 && (
                <span
                  className={cls.unreadBadge}
                  aria-label="Непрочитанные сообщения"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </FlexRow>
            <span
              className={`${cls.onlineLabel} ${
                isMainPage ? cls.onlineLabelInverse : ""
              }`}
            >
              <span className={cls.onlineDot} />
              Онлайн: {onlineCount}
            </span>
          </div>

          <FlexRow gap={12} alignItems="center" className={cls.headerActions}>
            {!isMobile &&
              desktopExpanded !== undefined &&
              (isFullscreen
                ? onDesktopFullscreenExit
                : onDesktopFullscreenRequest) && (
                <button
                  type="button"
                  className={cls.fullscreenButton}
                  aria-label={
                    isFullscreen
                      ? "Выйти из полноэкранного режима"
                      : "Во весь экран"
                  }
                  title={
                    isFullscreen
                      ? "Выйти из полноэкранного режима"
                      : "Во весь экран"
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    if (isFullscreen) onDesktopFullscreenExit?.();
                    else onDesktopFullscreenRequest?.();
                  }}
                >
                  <FullscreenIcon fullscreen={isFullscreen} />
                </button>
              )}
            <button
              type="button"
              className={`${cls.soundButton} ${
                isMainPage ? cls.soundButtonInverse : ""
              }`}
              onClick={(event) => {
                event.stopPropagation();
                toggleSound();
              }}
              aria-label={soundEnabled ? "Выключить звук" : "Включить звук"}
              title={soundEnabled ? "Выключить звук" : "Включить звук"}
            >
              <SoundIcon enabled={soundEnabled} />
            </button>
            <button
              type="button"
              className={cls.chatToggleButton}
              data-chat-toggle
              aria-label={
                docked
                  ? "Открыть чат во весь экран"
                  : chatIsVisible
                  ? "Свернуть чат"
                  : "Развернуть чат"
              }
              title={
                docked
                  ? "Открыть чат во весь экран"
                  : chatIsVisible
                  ? "Скрыть"
                  : "Открыть"
              }
              aria-expanded={chatIsVisible}
              aria-controls={messagesId}
              onClick={(event) => {
                event.stopPropagation();
                toggleChat();
              }}
            >
              <FlexRow gap={4} alignItems="center">
                {!docked && (
                  <Typography
                    size="xl"
                    color={isMainPage ? "#fff" : "var(--text-dark-primary)"}
                    className={cls.headerBtn}
                  >
                    {chatIsVisible ? "Скрыть" : "Открыть"}
                  </Typography>
                )}
                <span
                  className={
                    chatIsVisible ? cls.openChatIcon : cls.hideChatIcon
                  }
                >
                  <ChatArrow />
                </span>
              </FlexRow>
            </button>
          </FlexRow>
        </div>

        {chatIsVisible && (
          <>
            <div
              className={cls.messagesWrapper}
              id={messagesId}
              ref={listRef}
              data-dropdown-boundary
              data-chat-empty={messages.length === 0}
            >
              <div className={cls.messagesList}>
                {isLoading && messages.length === 0 && (
                  <span className={cls.chatStatus}>Загружаем сообщения…</span>
                )}
                {!isLoading && messages.length === 0 && (
                  <span className={cls.chatStatus}>Пока сообщений нет</span>
                )}
                {grouped.map((group) => (
                  <div
                    key={group.key}
                    className={group.isOwn ? cls.groupOwn : cls.groupForeign}
                  >
                    {!group.isOwn && (
                      <div className={cls.avatarCol}>
                        <img
                          src="/images/user_placeholder.png"
                          alt={group.username}
                          className={cls.avatarImage}
                        />
                      </div>
                    )}
                    <div className={cls.bubblesCol}>
                      {group.items.map((message, index) => (
                        <ChatMessage
                          key={message.id}
                          message={message}
                          index={index}
                          isHighlighted={highlightedMessageId === message.id}
                          onReplyPreviewClick={handleReplyPreviewClick}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={cls.inputBar}>
              {isEditing && (
                <div className={cls.composerContext}>
                  <div>
                    <span className={cls.composerContextTitle}>
                      Редактирование сообщения
                    </span>
                  </div>
                  <button type="button" onClick={cancelEdit}>
                    Отмена
                  </button>
                </div>
              )}
              {replyingTo && !isEditing && (
                <div className={cls.composerContext}>
                  <div className={cls.composerContextText}>
                    <span className={cls.composerContextTitle}>
                      Ответ для {getPublicChatUsername(replyingTo.username)}
                    </span>
                    <span>{getReplyLabel(replyingTo)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={cancelReply}
                    aria-label="Отменить ответ"
                  >
                    ×
                  </button>
                </div>
              )}

              {isAuthenticated && (
                <FlexRow
                  gap={12}
                  alignItems="center"
                  className={cls.inputBarBlock}
                >
                  <div className={cls.inputWrapper}>
                    <Textarea
                      placeholder={
                        replyingTo ? "Написать ответ" : "Написать в чат"
                      }
                      value={isEditing ? editingValue : input}
                      onChange={(value) =>
                        isEditing ? setEditingValue(value) : setInput(value)
                      }
                      onKeyDown={handleKeyDown}
                      rows={1}
                    />
                  </div>

                  <FlexRow gap={8} alignItems="center">
                    <div className={cls.attachBlock} ref={attachBlockRef}>
                      <Button
                        variant="iconOnly"
                        icon={<AttachIcon />}
                        onClick={() =>
                          setAttachBlockIsOpen((current) => !current)
                        }
                      />
                      {attachBlockIsOpen && (
                        <ShareSurveyBlock
                          setAttachBlockIsOpen={setAttachBlockIsOpen}
                        />
                      )}
                    </div>
                    <Button
                      variant="iconOnly"
                      icon={<PaperPlaneIcon />}
                      onClick={() => void submit()}
                      disabled={sendDisabled}
                    />
                  </FlexRow>
                </FlexRow>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
